import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { 
  Card, 
  Typography, 
  Input, 
  Row, 
  Col,
  Collapse
} from "antd";
import { TableComponent } from "../Master/CommonComponents";
import Heading from "../../components/Heading";
import search_icon from "../../assests/images/search-icon.svg";
import "./style.scss";

const { Text } = Typography;
const { Panel } = Collapse;

const CPSearch = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchData, setSearchData] = useState([]);
  const [filteredPincodeData, setFilteredPincodeData] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [cpDetails, setCpDetails] = useState(null);
  const [hubDetails, setHubDetails] = useState(null);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
    showSizeChanger: true,
    showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`
  });

  // Extract data from navigation state
  useEffect(() => {
    const { cpData } = location.state || {};
    if (cpData && Array.isArray(cpData)) {
      setSearchData(cpData);
      
      // Extract first item's details for display
      if (cpData.length > 0) {
        const firstItem = cpData[0];
        setCpDetails(firstItem.cpDetails);
        setHubDetails(firstItem.hubDetails);
        
        // Flatten all pincode details from all items
        const allPincodeDetails = cpData.flatMap(item => 
          item.pincodeDetails?.map(pincode => ({
            ...pincode,
            cpName: item.cpDetails?.cpName,
            hubName: item.hubDetails?.name,
            cpId: item.cpDetails?.cpId,
            hubId: item.hubDetails?.hubId
          })) || []
        );
        
        setFilteredPincodeData(allPincodeDetails);
        setPagination(prev => ({
          ...prev,
          total: allPincodeDetails.length
        }));
      }
    }
  }, [location.state]);

  // Filter pincode data based on search criteria
  useEffect(() => {
    if (!searchData.length) return;
    
    const allPincodeDetails = searchData.flatMap(item => 
      item.pincodeDetails?.map(pincode => ({
        ...pincode,
        cpName: item.cpDetails?.cpName,
        hubName: item.hubDetails?.name,
        cpId: item.cpDetails?.cpId,
        hubId: item.hubDetails?.hubId
      })) || []
    );

    let filtered = [...allPincodeDetails];

    // Text search
    if (searchText) {
      filtered = filtered.filter(item =>
        item.servicableArea?.toLowerCase().includes(searchText.toLowerCase()) ||
        item.pincode?.toLowerCase().includes(searchText.toLowerCase()) ||
        item.cpName?.toLowerCase().includes(searchText.toLowerCase()) ||
        item.hubName?.toLowerCase().includes(searchText.toLowerCase())
      );
    }

    setFilteredPincodeData(filtered);
    setPagination(prev => ({
      ...prev,
      total: filtered.length,
      current: 1
    }));
  }, [searchData, searchText]);

  // Table columns for CP Details
  const cpDetailsColumns = [
    {
      title: "CP Name",
      dataIndex: "cpName",
      key: "cpName",
      width: 120,
      render: (text) => <Text strong style={{ fontSize: "14px" }}>{text || "NA"}</Text>,
    },
    {
      title: "CP Type",
      dataIndex: "cpType",
      key: "cpType",
      width: 150,
      render: (text) => <Text style={{ fontSize: "14px" }}>{text || "NA"}</Text>,
    },
    {
      title: "Address",
      dataIndex: "address",
      key: "address",
      width: 250,
      render: (text) => (
        <Text style={{ fontSize: "14px" }} ellipsis={{ tooltip: text }}>
          {text || "NA"}
        </Text>
      ),
    },
    {
      title: "Official Number",
      dataIndex: "officialNumber",
      key: "officialNumber",
      width: 130,
      render: (text) => <Text style={{ fontSize: "14px" }}>{text || "NA"}</Text>,
    },
    {
      title: "Personal Number",
      dataIndex: "personalNumber",
      key: "personalNumber",
      width: 130,
      render: (text) => <Text style={{ fontSize: "14px" }}>{text || "NA"}</Text>,
    },
    {
      title: "Official Email",
      dataIndex: "officialEmailId",
      key: "officialEmailId",
      width: 200,
      render: (text) => <Text style={{ fontSize: "14px" }}>{text || "NA"}</Text>,
    },
    {
      title: "City",
      dataIndex: "city",
      key: "city",
      width: 100,
      render: (text) => <Text style={{ fontSize: "14px" }}>{text || "NA"}</Text>,
    },
    {
      title: "State",
      dataIndex: "state",
      key: "state",
      width: 100,
      render: (text) => <Text style={{ fontSize: "14px" }}>{text || "NA"}</Text>,
    },
    {
      title: "Pincode",
      dataIndex: "pincode",
      key: "pincode",
      width: 100,
      fixed: "right",
      render: (text) => <Text style={{ fontSize: "14px" }}>{text || "NA"}</Text>,
    }
  ];

  // Table columns for Hub Details
  const hubDetailsColumns = [

    {
      title: "Hub Name",
      dataIndex: "name",
      key: "name",
      width: 150,
      render: (text) => <Text strong style={{ fontSize: "14px" }}>{text || "NA"}</Text>,
    },
    {
      title: "Address",
      dataIndex: "address",
      key: "address",
      width: 300,
      render: (text) => (
        <Text style={{ fontSize: "14px" }} ellipsis={{ tooltip: text }}>
          {text || "NA"}
        </Text>
      ),
    },
    {
      title: "Official Number",
      dataIndex: "officialNumber",
      key: "officialNumber",
      width: 140,
      render: (text) => <Text style={{ fontSize: "14px" }}>{text || "NA"}</Text>,
    },
    {
      title: "Personal Number",
      dataIndex: "personalNumber",
      key: "personalNumber",
      width: 140,
      render: (text) => <Text style={{ fontSize: "14px" }}>{text || "NA"}</Text>,
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      width: 200,
      fixed: "right",
      render: (text) => <Text style={{ fontSize: "14px" }}>{text || "NA"}</Text>,
    }
  ];

  // Table columns for Pincode Details
  const pincodeColumns = [
    {
      title: "No.",
      key: "index",
      width: 60,
      fixed: "left",
      render: (_, __, index) => (pagination.current - 1) * pagination.pageSize + index + 1,
    },
    {
      title: "Pincode",
      dataIndex: "pincode",
      key: "pincode",
      sorter: true,
      width: 120,
      render: (text) => <Text style={{ fontSize: "14px", fontWeight: "500" }}>{text || "NA"}</Text>,
    },
    {
      title: "Serviceable Area",
      dataIndex: "servicableArea",
      key: "servicableArea",
      sorter: true,
      width: 300,
      render: (text) => <Text style={{ fontSize: "14px" }}>{text || "NA"}</Text>,
    },
    {
      title: "CP Name",
      dataIndex: "cpName",
      key: "cpName",
      sorter: true,
      width: 150,
      render: (text) => <Text style={{ fontSize: "14px" }}>{text || "NA"}</Text>,
    },
    {
      title: "Hub Name",
      dataIndex: "hubName",
      key: "hubName",
      sorter: true,
      width: 150,
      fixed: "right",
      render: (text) => <Text style={{ fontSize: "14px" }}>{text || "NA"}</Text>,
    }
  ];

  // Prepare CP details data for table
  const cpDetailsData = cpDetails ? [cpDetails] : [];

  // Prepare Hub details data for table
  const hubDetailsData = hubDetails ? [hubDetails] : [];

  const searchTerm = location.state?.searchTerm || "";

  // Custom back handler
  const goBackToPrevScreen = () => {
    if (window.history.length <= 1) {
      window.close();
    } else {
      navigate(-1);
    }
  };

  // Handle table change for pagination, sorting, etc.
  const handleTableChange = (newPagination) => {
    setPagination(newPagination);
  };

  return (
    <>
      <div className="p-2-tab">
        <Heading title="CP Search Results" showBack={true} goBack={goBackToPrevScreen} />
        
        {/* Main Summary Card */}
        <Card
          style={{ borderRadius: 10, marginBottom: 20, marginTop: 20 }}
          bodyStyle={{ padding: 20 }}
        >
          <div className="space-between-start">
            <Row gutter={[16, 16]} align="middle">
              <Col xs={24}>
                <div style={{ display: "flex", alignItems: "center", gap: "50px", flexWrap: "nowrap" }}>
                  <div>
                    <Text strong style={{ fontSize: "18px" }}>Search: {searchTerm}</Text>
                  </div>
                  <div style={{ flex: 1, minWidth: "300px", paddingTop: "10px" }}>
                    <Input
                      placeholder="Search by pincode, serviceable area, CP name..."
                      bordered
                      prefix={<img src={search_icon} />}
                      style={{
                        borderRadius: "5px",
                        height: "40px",
                      }}
                      value={searchText}
                      onChange={(e) => setSearchText(e.target.value)}
                      allowClear
                    />
                  </div>
                </div>
              </Col>
            </Row>
          </div>
        </Card>

        {/* Details Section */}
        <Collapse defaultActiveKey={["1", "2", "3"]} style={{ marginBottom: 20 }}>
          <Panel header="CP Details" key="1">
            <TableComponent
              columns={cpDetailsColumns}
              dataSource={cpDetailsData}
              loading={false}
              pagination={false}
              rowKey={(record) => `cp-${record.cpId}`}
              size="small"
              scroll={{ x: 1200 }}
            />
          </Panel>
          
          <Panel header="Hub Details" key="2">
            <TableComponent
              columns={hubDetailsColumns}
              dataSource={hubDetailsData}
              loading={false}
              pagination={false}
              rowKey={(record) => `hub-${record.hubId}`}
              size="small"
              scroll={{ x: 1000 }}
            />
          </Panel>
          
          <Panel header={`Pincode Details (${filteredPincodeData.length} areas)`} key="3">
            <TableComponent
              columns={pincodeColumns}
              dataSource={filteredPincodeData}
              loading={false}
              pagination={pagination}
              onChange={handleTableChange}
              rowKey={(record, index) => `${record.pincode}-${record.servicableArea}-${index}`}
              scroll={{ x: 800 }}
            />
          </Panel>
        </Collapse>
      </div>
    </>
  );
};

export default CPSearch; 
